import { useContext, useEffect, useMemo, useState } from "react";
import { ClipboardList, Gauge, TrendingUp, Plus } from "lucide-react";
import { VerifyContext } from "app/VerifyContext";
import Button from "shared/Button";
import KpiCard from "shared/KpiCard";
import Alert from "shared/Alert";
import ConfirmModal from "shared/ConfirmModal";

import {
  deleteEmployeeEvaluation,
  fetchEmployeeEvaluations,
  postEmployeeEvaluation,
  updateEmployeeEvaluation,
} from "./api/evaluate";
import type { EvaluationItem, EvaluationPayload } from "./modal/evaluate_context";
import { CRITERIA, dateOnly, evaluationAverage, formatDate, scoreTone, sortNewestFirst } from "./ui/evaluationConfig";
import EvaluationCard from "./ui/EvaluationCard";
import EvaluationForm from "./ui/EvaluationForm";

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const emptyForm = (): EvaluationPayload => ({
  task_completion: 0,
  team_player: 0,
  time_management: 0,
  positive_work_attitide: 0,
  adaptable_and_flexible: 0,
  ability_to_learn: 0,
  problem_solving: 0,
  punctuality: 0,
  general_comments: "",
  extra_comments: "",
  created_at: todayLocal(),
});

interface EvaluationPanelProps {
  employeeId: number;
  employeeName: string;
  employeeSubtitle?: string;
}

/** Right pane: the selected employee's evaluations. Remount (key) per employee. */
export default function EmployeeEvaluationDetails({ employeeId, employeeName: listName, employeeSubtitle }: EvaluationPanelProps) {
  const { superAdmin, user } = useContext(VerifyContext);
  const [employeeName, setEmployeeName] = useState(listName);
  const [evaluations, setEvaluations] = useState<EvaluationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [formInitial, setFormInitial] = useState<EvaluationPayload | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const numericEmployeeId = employeeId;
  const roleNames = user?.roles || [];
  const canCreate = (superAdmin || roleNames.includes("HR") || roleNames.includes("Team Lead")) && numericEmployeeId !== user?.id;
  const canEditDelete = superAdmin;

  const loadEvaluations = async () => {
    if (!numericEmployeeId) return;
    setLoading(true);
    setMessage("");
    try {
      const data = await fetchEmployeeEvaluations(numericEmployeeId);
      setEvaluations(sortNewestFirst(data.evaluations || []));
      setEmployeeName(data.employee?.name || `Employee #${numericEmployeeId}`);
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "Unable to load evaluations.");
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvaluations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericEmployeeId]);

  const { latestAvg, delta } = useMemo(() => {
    const latest = evaluations[0] ? evaluationAverage(evaluations[0]) : null;
    const previous = evaluations[1] ? evaluationAverage(evaluations[1]) : null;
    return { latestAvg: latest, delta: latest != null && previous != null ? latest - previous : null };
  }, [evaluations]);

  function openCreate() {
    setEditingId(null);
    setFormInitial(emptyForm());
  }

  function openEdit(evaluation: EvaluationItem) {
    setEditingId(evaluation.evaluation_id);
    setFormInitial({
      ...Object.fromEntries(CRITERIA.map(({ key }) => [key, evaluation[key]])) as Pick<EvaluationPayload, typeof CRITERIA[number]["key"]>,
      general_comments: evaluation.general_comments || "",
      extra_comments: evaluation.extra_comments || "",
      created_at: dateOnly(evaluation.created_at) || todayLocal(),
    });
  }

  async function saveEvaluation(payload: EvaluationPayload) {
    if (editingId) await updateEmployeeEvaluation(numericEmployeeId, editingId, payload);
    else await postEmployeeEvaluation(numericEmployeeId, payload);
    setFormInitial(null);
    setEditingId(null);
    await loadEvaluations();
  }

  async function confirmDelete() {
    if (deletingId === null) return;
    try {
      await deleteEmployeeEvaluation(numericEmployeeId, deletingId);
      await loadEvaluations();
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "Unable to delete evaluation.");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{employeeName || "Evaluations"}</h2>
          {employeeSubtitle && <p className="text-sm text-muted-foreground">{employeeSubtitle}</p>}
        </div>
        {canCreate && (
          <Button variant="primary" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1.5 inline" />
            New evaluation
          </Button>
        )}
      </div>

      {message && <Alert tone="danger">{message}</Alert>}

      {evaluations.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard label="Evaluations" value={evaluations.length} icon={ClipboardList} />
          <KpiCard
            label="Latest score"
            value={latestAvg == null ? "—" : latestAvg.toFixed(1)}
            hint={`out of 5 · ${formatDate(evaluations[0].created_at)}`}
            icon={Gauge}
            tone={latestAvg == null ? undefined : scoreTone(latestAvg)}
          />
          <KpiCard
            label="Change since previous"
            value={delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}`}
            hint={delta == null ? "Needs two evaluations" : delta === 0 ? "No change" : delta > 0 ? "Improved" : "Declined"}
            icon={TrendingUp}
            tone={delta == null || delta === 0 ? undefined : delta > 0 ? "success" : "danger"}
          />
        </div>
      )}

      {loading && evaluations.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !loading && evaluations.length === 0 && !message ? (
        <div className="flex flex-col items-center justify-center py-16 bg-surface border border-dashed border-border rounded-xl text-muted-foreground">
          <p className="text-sm">No evaluations yet.</p>
          {canCreate && (
            <button onClick={openCreate} className="mt-3 text-primary hover:text-primary/80 text-sm underline">Create the first one</button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {evaluations.map((evaluation) => (
            <EvaluationCard
              key={evaluation.evaluation_id}
              evaluation={evaluation}
              canEditDelete={canEditDelete}
              onEdit={openEdit}
              onDelete={setDeletingId}
            />
          ))}
        </div>
      )}

      {formInitial && (
        <EvaluationForm
          employeeName={employeeName}
          initial={formInitial}
          editing={editingId !== null}
          onSave={saveEvaluation}
          onClose={() => { setFormInitial(null); setEditingId(null); }}
        />
      )}

      {deletingId !== null && (
        <ConfirmModal
          title="Delete evaluation?"
          message="This permanently removes the evaluation. This cannot be undone."
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          onCancel={() => setDeletingId(null)}
        />
      )}
    </div>
  );
}
